import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Send, MessageSquare, Bot, User, Loader2, Paperclip, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error loading chat history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    
    if (!inputMessage.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        message: inputMessage
      });
      
      const botMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      toast.error("Error al enviar mensaje");
      // Remove the user message if there was an error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result;
      
      try {
        setLoading(true);
        const response = await axios.post(`${API}/clients/import`, {
          content: content,
          file_type: file.type
        });
        
        toast.success(`Importación completada: ${response.data.created} clientes creados, ${response.data.skipped} omitidos`);
        
        // Add a message about the import
        const importMessage = {
          id: Date.now(),
          role: "assistant",
          content: `📁 He analizado el archivo "${file.name}".\n\n✅ Clientes creados: ${response.data.created}\n⏭️ Clientes omitidos (ya existían): ${response.data.skipped}\n📊 Total encontrados: ${response.data.total_found}${response.data.errors.length > 0 ? `\n\n⚠️ Errores: ${response.data.errors.join(", ")}` : ""}`,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, importMessage]);
      } catch (error) {
        toast.error("Error al importar archivo");
      } finally {
        setLoading(false);
      }
    };
    
    reader.readAsText(file);
    e.target.value = "";
  };

  const formatMessage = (content) => {
    // Convert markdown-like formatting to HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  };

  if (loadingHistory) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare className="w-7 h-7 text-[#5B9BD5]" />
          Asistente de Facturación
        </h1>
        <p className="text-slate-600">
          Crea facturas de forma conversacional. Escribe como si hablaras con un colega.
        </p>
      </div>

      {/* Chat Container */}
      <Card className="cia-card flex-1 flex flex-col overflow-hidden">
        <CardHeader className="border-b border-slate-100 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5B9BD5] flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Factu</CardTitle>
              <p className="text-xs text-green-600">En línea</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 p-0 flex flex-col overflow-hidden">
          {/* Messages Area */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <Bot className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">¡Hola! Soy Factu</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Tu asistente de facturación. Puedo ayudarte a crear facturas, 
                    dar de alta clientes y más. Solo dime qué necesitas.
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {[
                      "Quiero hacer una factura",
                      "Dame de alta un cliente",
                      "¿Qué datos necesitas para facturar?"
                    ].map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => setInputMessage(suggestion)}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm text-slate-700 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {messages.map((message) => (
                <div
                  key={message.id || message.timestamp}
                  className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === "user" 
                      ? "bg-[#5B9BD5]" 
                      : "bg-slate-200"
                  }`}>
                    {message.role === "user" 
                      ? <User className="w-4 h-4 text-white" />
                      : <Bot className="w-4 h-4 text-slate-600" />
                    }
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === "user"
                      ? "bg-[#5B9BD5] text-white"
                      : "bg-slate-100 text-slate-800"
                  }`}>
                    <div 
                      className="text-sm whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                    />
                    <p className={`text-xs mt-1 ${
                      message.role === "user" ? "text-white/70" : "text-slate-400"
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString('es-MX', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      <span className="text-sm text-slate-500">Escribiendo...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t border-slate-100 p-4">
            <form onSubmit={sendMessage} className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept=".csv,.txt,.xlsx,.xls,.json"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0"
                title="Importar clientes desde archivo"
                data-testid="upload-file-btn"
              >
                <Paperclip className="w-5 h-5 text-slate-500" />
              </Button>
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="flex-1 cia-input"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                className="cia-btn-primary"
                disabled={loading || !inputMessage.trim()}
                data-testid="send-message-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
            <p className="text-xs text-slate-400 mt-2 text-center">
              Tip: Puedes adjuntar archivos CSV o Excel para importar clientes
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatPage;
